/**
 * ProfileV2 — Sprint C (Feb 2026)
 *
 * 11-subsection personal-profile redesign per
 * /app/mobile-handoff/PROFILE_V2_SPEC.md.
 *
 * Feature-flagged behind `?v2=1` in App.js routing; the original
 * `Profile.jsx` remains the default until we flip it.
 *
 * Data sources — all existing endpoints (no backend work):
 *   GET /profile
 *   GET /v2/scores/me (Meet + Responsiveness)
 *   GET /profile-score
 *   GET /ghosting-score
 *   GET /subscriptions/my
 *   GET /store/inventory (purchased_super_likes + purchased_boosts)
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api, useAuth } from '@/contexts/AuthContext';
import {
  Share2, Settings, HelpCircle, ChevronRight, X, Bell, Camera, Copy, Check,
  Heart, Sparkles, Leaf, HandMetal, PlusCircle, Circle, Zap, Star, Diamond,
  Wallet, Gauge, Plus, Eye, ArrowUndo, Undo2, CheckSquare, ShieldCheck, Globe,
  Ban, ShieldBan, MapPin, Crown, Lock, ArrowLeft,
} from 'lucide-react';

const APP_BG = '#F5F5F7';

/* ------------------------------------------------------------------ */
/* Photo URL helper (matches other pages)                              */
/* ------------------------------------------------------------------ */
function getPhotoUrl(p) {
  if (!p) return null;
  if (p.startsWith('http')) return p;
  return `${window.location.origin}/api/files/${p}`;
}

/* ------------------------------------------------------------------ */
/* Score circle (small ring + centered "N/100" text)                   */
/* ------------------------------------------------------------------ */
function ScoreCircle({ score, color = '#DC2626', label = '/100', kicker, subtitle, size = 88, testid, onClick }) {
  const s = Math.max(0, Math.min(100, score || 0));
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (s / 100) * c;
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="bg-white rounded-2xl p-4 flex items-center gap-3 border border-[var(--hairline)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition-all w-full text-left"
    >
      <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#F1F1F3" strokeWidth={6} />
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={6}
            strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" style={{ transition: 'stroke-dashoffset 700ms' }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center flex-col leading-none">
          <span className="font-display font-[800] text-[18px] text-[#0F0F10]">{Math.round(s)}</span>
          <span className="text-[9px] font-semibold text-[#8A8A93]">{label}</span>
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold text-[#8A8A93] uppercase tracking-[0.12em]">{kicker}</p>
        <p className="text-[13px] font-semibold text-[#0F0F10] mt-0.5 truncate">{subtitle}</p>
      </div>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Intent chip mapping (spec 5.2a).                                    */
/* ------------------------------------------------------------------ */
const INTENT_MAP = {
  long_term:    { label: 'Long-term relationship', icon: Heart,       bg: '#FEE2E2', fg: '#B91C1C' },
  life_partner: { label: 'Ready for a relationship', icon: Heart,     bg: '#FEE2E2', fg: '#B91C1C' },
  marriage:     { label: 'Ready for marriage',     icon: Heart,       bg: '#FCE7F3', fg: '#BE185D' },
  casual:       { label: 'Casual dating',          icon: Sparkles,    bg: '#FEF3C7', fg: '#B45309' },
  figuring_out: { label: 'Taking it slow',         icon: Leaf,        bg: '#DCFCE7', fg: '#166534' },
  friends:      { label: 'New friends',            icon: HandMetal,   bg: '#E0F2FE', fg: '#0369A1' },
};

/* ------------------------------------------------------------------ */
/* Feature comparison table config (spec 5.10a).                       */
/* ------------------------------------------------------------------ */
const COMPARE_ROWS = [
  { icon: Eye,            tint: '#DBEAFE', label: 'See who likes you',   free: '—',      premium: '\u2713',   vip: '\u2713' },
  { icon: Zap,            tint: '#EDE9FE', label: 'Boosts',              free: '—',      premium: '5/mo',     vip: 'Unlim' },
  { icon: Star,           tint: '#FEF3C7', label: 'Super Likes',         free: '1/day',  premium: '5/day',    vip: 'Unlim' },
  { icon: Sparkles,       tint: '#DCFCE7', label: 'Advanced filters',    free: '—',      premium: '\u2713',   vip: '\u2713' },
  { icon: Undo2,          tint: '#FCE7F3', label: 'Rewind',              free: '—',      premium: '\u2713',   vip: '\u2713' },
  { icon: Check,          tint: '#FEE2E2', label: 'Read receipts',       free: '—',      premium: '\u2713',   vip: '\u2713' },
  { icon: ShieldCheck,    tint: '#F3E8FF', label: 'Incognito mode',      free: '—',      premium: '—',        vip: '\u2713' },
  { icon: Globe,          tint: '#E0F2FE', label: 'Global search',       free: '—',      premium: '—',        vip: '\u2713' },
  { icon: Ban,            tint: '#F4F4F5', label: 'No ads',              free: '—',      premium: '\u2713',   vip: '\u2713' },
];

/* ================================================================== */
export default function ProfileV2() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [sub, setSub] = useState(null);
  const [v2Scores, setV2Scores] = useState(null);
  const [profileScore, setProfileScore] = useState(null);
  const [ghosting, setGhosting] = useState(null);
  const [inventory, setInventory] = useState({ super_likes: 0, boosts: 0 });
  const [loading, setLoading] = useState(true);

  // Session-only dismiss for the two nudges (per spec).
  const [notifyDismissed, setNotifyDismissed] = useState(false);
  const [openerDismissed, setOpenerDismissed] = useState(false);

  // ID copy state
  const [idCopied, setIdCopied] = useState(false);

  // Tabs (Plans / Safety)
  const [tab, setTab] = useState('plans');

  // Premium promo carousel index (FREE only)
  const [promoIdx, setPromoIdx] = useState(0);

  useEffect(() => {
    Promise.all([
      api.get('/profile').then(r => setProfile(r.data)).catch(() => {}),
      api.get('/subscriptions/my').then(r => setSub(r.data)).catch(() => {}),
      api.get('/v2/scores/me').then(r => setV2Scores(r.data)).catch(() => {}),
      api.get('/profile-score').then(r => setProfileScore(r.data)).catch(() => {}),
      api.get('/ghosting-score').then(r => setGhosting(r.data)).catch(() => {}),
      api.get('/store/inventory').then(r => setInventory({
        super_likes: r.data.purchased_super_likes || 0,
        boosts: r.data.purchased_boosts || 0,
      })).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const tier = (sub?.tier || 'free').toLowerCase();
  const photos = profile?.photos?.length ? profile.photos : (profile?.photo_url ? [profile.photo_url] : []);
  const strength = useMemo(() => {
    if (!profile) return 0;
    const bio = profile.bio ? 25 : 0;
    const phot = Math.min(50, photos.length * 10);
    const answers = Object.keys(profile.answers || {}).length * 2.5;
    return Math.min(100, Math.round(bio + phot + answers));
  }, [profile, photos.length]);

  const answers = profile?.answers || {};
  const intent = answers.relationship_goals || profile?.intent;
  const intentCfg = intent ? INTENT_MAP[intent] : null;

  const credits = inventory.super_likes + inventory.boosts;

  const activityScore = v2Scores?.responsiveness?.score || 0;
  const activityLabel =
    activityScore >= 80 ? 'Very high' :
    activityScore >= 60 ? 'High' :
    activityScore >= 40 ? 'Medium' :
    activityScore >= 20 ? 'Low' : 'Very low';

  const isFree = tier === 'free';
  const shouldShowOpener = !openerDismissed && Object.keys(answers).length < 2;

  const copyUserId = async () => {
    if (!profile?.user_id) return;
    try {
      await navigator.clipboard.writeText(profile.user_id);
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  if (loading || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: APP_BG }}>
        <div className="w-8 h-8 rounded-full border-2 border-[#DC2626] border-t-transparent animate-spin" />
      </div>
    );
  }

  const badges = profile.badges || [];
  const isVerified = badges.includes('verified');
  // "Online now" heuristic — server sets last_active or is_online
  const isOnline = profile.is_online === true;

  return (
    <div className="min-h-screen pb-24" style={{ background: APP_BG }} data-testid="profile-v2-page">
      {/* ======================= 5.1 Top App Bar ======================= */}
      <div className="px-4 pt-6 pb-3 flex items-end justify-between" data-testid="profile-v2-top-bar">
        <h1 className="font-display font-[900] text-[32px] tracking-[-0.03em] text-[#0F0F10] leading-none">Profile</h1>
        <div className="flex gap-2">
          {[
            { icon: Share2, label: 'Share', onClick: async () => {
              try {
                if (navigator.share) {
                  await navigator.share({ title: `${profile.display_name} on Meet to Keep`, text: 'Check out my profile', url: window.location.href });
                } else {
                  await navigator.clipboard.writeText(window.location.origin);
                  toast.success('Link copied');
                }
              } catch { /* user cancelled */ }
            }},
            { icon: Settings, label: 'Settings', onClick: () => navigate('/settings') },
            { icon: HelpCircle, label: 'Help',   onClick: () => navigate('/support') },
          ].map(({ icon: Icon, label, onClick }) => (
            <button
              key={label}
              onClick={onClick}
              aria-label={label}
              data-testid={`profile-v2-topbar-${label.toLowerCase()}`}
              className="w-10 h-10 rounded-full bg-white border border-[rgba(15,15,16,0.06)] flex items-center justify-center hover:bg-[#FAFAFA] transition-colors"
            >
              <Icon size={17} className="text-[#0F0F10]" strokeWidth={2} />
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 space-y-4">
        {/* ======================= 5.2 Identity Row ===================== */}
        <div className="flex gap-[14px] items-center" data-testid="profile-v2-identity-row">
          {/* Avatar with % pill + camera dot */}
          <div className="relative flex-shrink-0" style={{ width: 92, height: 92 }}>
            <div className="w-full h-full rounded-full overflow-hidden bg-[#0F0F10]">
              {photos[0] ? (
                <img src={getPhotoUrl(photos[0])} alt="" className="w-full h-full object-cover" />
              ) : null}
            </div>
            {/* % pill INSIDE avatar bottom */}
            <div
              data-testid="profile-v2-completion-pill"
              className="absolute left-1/2 -translate-x-1/2 text-white text-[11px] font-[900] tracking-wider px-2 py-[3px] rounded-full"
              style={{ background: 'rgba(15,15,16,0.88)', bottom: 6 }}
            >
              {strength}%
            </div>
            {/* Camera dot */}
            <button
              onClick={() => navigate('/profile?edit=1')}
              aria-label="Change photo"
              data-testid="profile-v2-camera-dot"
              className="absolute -bottom-1 -right-1 w-[26px] h-[26px] rounded-full bg-[#0F0F10] flex items-center justify-center border-2"
              style={{ borderColor: APP_BG }}
            >
              <Camera size={11} className="text-white" />
            </button>
          </div>

          {/* Right column */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 mb-1 flex-wrap" data-testid="profile-v2-name-row">
              <span className="font-display text-[22px] font-[900] tracking-[-0.5px] text-[#0F0F10] truncate">
                {profile.display_name}{profile.age ? `, ${profile.age}` : ''}
              </span>
              {isVerified && (
                <span className="ml-1 inline-flex items-center" aria-label="Verified" data-testid="profile-v2-verified">
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="#3B82F6">
                    <path d="M10 0l2.4 2.1 3.2-.4.5 3.2 2.9 1.4-1.3 3 1.3 3-2.9 1.4-.5 3.2-3.2-.4L10 20l-2.4-2.1-3.2.4-.5-3.2L1 13.7l1.3-3L1 7.7l2.9-1.4.5-3.2 3.2.4L10 0z"/>
                    <path d="M6.5 10l2.3 2.3L14 7" stroke="#fff" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              )}
              {isOnline && <span className="w-2 h-2 rounded-full ml-1.5" style={{ background: '#22C55E' }} title="Online" />}
            </div>

            {/* 5.2a Dating-intent chip */}
            <button
              onClick={() => navigate('/profile?edit=1')}
              data-testid="profile-v2-intent-chip"
              className="inline-flex items-center gap-[5px] rounded-full mb-2 px-2.5 py-1 max-w-full"
              style={
                intentCfg
                  ? { background: intentCfg.bg, color: intentCfg.fg }
                  : { background: '#F4F4F5', color: '#6B7280', border: '1px dashed rgba(15,15,16,0.15)' }
              }
            >
              {intentCfg
                ? <intentCfg.icon size={12} strokeWidth={2.4} fill={intentCfg.fg}/>
                : <PlusCircle size={12} strokeWidth={2}/>}
              <span className="text-[12px] font-[800] tracking-[-0.1px] truncate">
                {intentCfg ? intentCfg.label : 'Set your intent'}
              </span>
            </button>

            {profile.location?.city && (
              <div className="flex items-center gap-1 mb-1.5" data-testid="profile-v2-location">
                <MapPin size={12} className="text-[#6B7280]"/>
                <span className="text-[12px] font-medium text-[#6B7280]">{profile.location.city}</span>
              </div>
            )}

            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/profile?edit=1')}
                data-testid="profile-v2-edit-pill"
                className="rounded-full bg-[#0F0F10] text-white text-[13px] font-[700] px-[14px] py-[8px]"
              >
                Edit Profile
              </button>
            </div>
          </div>
        </div>

        {/* ID chip (copyable) */}
        <button
          onClick={copyUserId}
          data-testid="profile-v2-id-chip"
          className="inline-flex items-center gap-1.5 text-[11px] font-mono text-[#6B7280] hover:text-[#0F0F10] transition-colors -mt-1"
        >
          {idCopied
            ? <><Check size={11} className="text-[#16A34A]"/> <span className="text-[#16A34A]">Copied</span></>
            : <><Copy size={11}/> <span>ID: {(profile.user_id || '').slice(0, 10)}…</span></>}
        </button>

        {/* ======================= 5.3 Badges Row ======================= */}
        <div className="flex flex-wrap gap-1.5" data-testid="profile-v2-badges-row">
          {badges.includes('intentional_dater') && <span className="badge badge-intent">INTENTIONAL DATER</span>}
          {badges.includes('complete_profile') && <span className="badge badge-complete">COMPLETE</span>}
          {isVerified && <span className="badge" style={{ background: 'linear-gradient(135deg, #60A5FA, #3B82F6)', color: '#fff', border: '1px solid #1E40AF' }}>VERIFIED</span>}
        </div>

        {/* ======================= 5.4 Progress Bar ===================== */}
        <div className="pt-2" data-testid="profile-v2-progress">
          <div className="relative h-1.5 rounded-full bg-[#E5E7EB]">
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.max(strength, 4)}%`,
                background: 'linear-gradient(90deg, #FF7A85 0%, #DC2626 100%)',
              }}
            />
            {/* Floating chip */}
            <div
              className="absolute rounded-full text-white text-[10px] font-[900] px-2 py-[3px] tracking-[0.2px]"
              style={{
                background: '#DC2626',
                top: -10,
                left: `${Math.min(Math.max(strength - 8, 0), 90)}%`,
                boxShadow: '0 2px 6px rgba(220,38,38,0.35)',
              }}
            >
              {strength}%
            </div>
          </div>
          <p className="text-[13px] font-medium text-[#6B7280] mt-3.5">
            {strength >= 100 ? 'Your profile is 100% complete!' : 'Complete your profile to be seen by more people!'}
          </p>
        </div>

        {/* ======================= 5.5 Get-Notified Nudge =============== */}
        {!notifyDismissed && (
          <button
            onClick={() => navigate('/settings')}
            data-testid="profile-v2-notify-card"
            className="w-full text-left relative bg-white rounded-[18px] p-3.5 border border-[rgba(15,15,16,0.06)] flex items-center gap-3 hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition-shadow"
          >
            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#EDE9FE' }}>
              <Bell size={18} className="text-[#7C3AED]" strokeWidth={2.2} />
            </div>
            <span className="text-[14px] font-[700] text-[#0F0F10] tracking-[-0.2px] leading-[19px] flex-1 line-clamp-2">
              Get notified when you get a new match or message!
            </span>
            <ChevronRight size={18} className="text-[#6B7280] flex-shrink-0"/>
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); setNotifyDismissed(true); }}
              data-testid="profile-v2-notify-dismiss"
              className="absolute top-2 right-2 w-[22px] h-[22px] rounded-full flex items-center justify-center hover:bg-black/[0.06] transition-colors cursor-pointer"
              style={{ background: 'rgba(15,15,16,0.04)' }}
              aria-label="Dismiss"
            >
              <X size={12} className="text-[#6B7280]"/>
            </span>
          </button>
        )}

        {/* ======================= 5.6 Activity + Credits =============== */}
        <div className="grid grid-cols-2 gap-[10px]">
          <button
            onClick={() => navigate('/scoring-detail')}
            data-testid="profile-v2-activity-card"
            className="bg-white rounded-2xl p-3 flex gap-2.5 items-center border border-[var(--hairline)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition-shadow"
          >
            <div className="w-[38px] h-[38px] rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#FEE2E2' }}>
              <Gauge size={18} className="text-[#B91C1C]" strokeWidth={2.2}/>
            </div>
            <div className="text-left min-w-0">
              <p className="text-[11px] font-[700] tracking-[0.1px] uppercase text-[#6B7280]">Your activity</p>
              <p className="text-[15px] font-[900] text-[#0F0F10] mt-0.5 truncate">{activityLabel}</p>
            </div>
          </button>
          <button
            onClick={() => navigate('/store')}
            data-testid="profile-v2-credits-card"
            className="bg-white rounded-2xl p-3 flex gap-2.5 items-center border border-[var(--hairline)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition-shadow"
          >
            <div className="relative w-[38px] h-[38px] rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#EDE9FE' }}>
              <Wallet size={18} className="text-[#7C3AED]" strokeWidth={2.2}/>
              <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center border-2 border-white" style={{ background: '#DC2626' }}>
                <Plus size={10} className="text-white" strokeWidth={3}/>
              </span>
            </div>
            <div className="text-left min-w-0">
              <p className="text-[11px] font-[700] tracking-[0.1px] uppercase text-[#6B7280]">Credits</p>
              <p className="text-[15px] font-[900] text-[#0F0F10] mt-0.5">{credits > 0 ? credits : 'Add'}</p>
            </div>
          </button>
        </div>

        {/* ======================= 5.7 Set Your Opener ================== */}
        {shouldShowOpener && (
          <div
            data-testid="profile-v2-opener-card"
            className="relative rounded-[18px] p-4"
            style={{ background: '#EDE9FE' }}
          >
            <button
              onClick={() => setOpenerDismissed(true)}
              data-testid="profile-v2-opener-dismiss"
              aria-label="Dismiss"
              className="absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center hover:bg-black/[0.06] transition-colors"
            >
              <X size={13} className="text-[#6B7280]"/>
            </button>
            <p className="text-[17px] font-[900] text-[#0F0F10]">Set your Opener</p>
            <p className="text-[13px] font-medium text-[#4B5563] leading-[18px] mt-1 mb-3.5">
              Add a question for your new matches to answer and start the chat
            </p>
            <button
              onClick={() => navigate('/profile?edit=1')}
              data-testid="profile-v2-opener-cta"
              className="rounded-full bg-[#0F0F10] text-white text-[13px] font-[700] px-[18px] py-[10px]"
            >
              Add Opener
            </button>
          </div>
        )}

        {/* ======================= 5.8 Premium Quick-Tiles ============== */}
        <div className="-mx-4 pl-4 pr-1 mt-2">
          <div
            data-testid="profile-v2-quick-tiles"
            className="flex gap-[10px] overflow-x-auto pb-1"
            style={{ scrollbarWidth: 'none' }}
          >
            {/* Tile 1 — Super Likes */}
            <button
              onClick={() => navigate('/store')}
              data-testid="profile-v2-tile-super"
              className="flex-shrink-0 w-[148px] bg-white rounded-2xl p-[14px] border border-[var(--hairline)] text-left hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition-shadow"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-[30px] h-[30px] rounded-lg flex items-center justify-center" style={{ background: '#0F0F10' }}>
                  <Star size={16} fill="#FDE047" className="text-[#FDE047]"/>
                </div>
                <div className="w-[22px] h-[22px] rounded-full flex items-center justify-center" style={{ background: '#F4F4F5' }}>
                  <Plus size={14} className="text-[#6B7280]"/>
                </div>
              </div>
              <p className="font-display font-[900] text-[20px] tracking-[-0.5px] text-[#0F0F10] leading-none">{inventory.super_likes} Super Likes</p>
              <p className="text-[11px] font-[600] text-[#6B7280] tracking-[-0.1px] mt-1">Get more</p>
            </button>

            {/* Tile 2 — Boosts */}
            <button
              onClick={() => navigate('/store')}
              data-testid="profile-v2-tile-boost"
              className="flex-shrink-0 w-[148px] bg-white rounded-2xl p-[14px] border border-[var(--hairline)] text-left hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition-shadow"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-[30px] h-[30px] rounded-lg flex items-center justify-center" style={{ background: '#0F0F10' }}>
                  <Zap size={16} fill="#A78BFA" className="text-[#A78BFA]"/>
                </div>
                <div className="w-[22px] h-[22px] rounded-full flex items-center justify-center" style={{ background: '#F4F4F5' }}>
                  <Plus size={14} className="text-[#6B7280]"/>
                </div>
              </div>
              <p className="font-display font-[900] text-[20px] tracking-[-0.5px] text-[#0F0F10] leading-none">{inventory.boosts} Boosts</p>
              <p className="text-[11px] font-[600] text-[#6B7280] tracking-[-0.1px] mt-1">Get more</p>
            </button>

            {/* Tile 3 — Plan (VIP / Premium / Free) */}
            <button
              onClick={() => navigate('/subscription')}
              data-testid="profile-v2-tile-plan"
              className="flex-shrink-0 w-[148px] bg-white rounded-2xl p-[14px] border border-[var(--hairline)] text-left hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition-shadow mr-3"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-[30px] h-[30px] rounded-lg flex items-center justify-center" style={{ background: '#0F0F10' }}>
                  {tier === 'vip' && <Diamond size={16} className="text-[#E0E7FF]" fill="#E0E7FF"/>}
                  {tier === 'premium' && <Star size={16} fill="#FDE047" className="text-[#FDE047]"/>}
                  {tier === 'free' && <Sparkles size={16} className="text-[#F9A8D4]"/>}
                </div>
                <ChevronRight size={16} className="text-[#6B7280]"/>
              </div>
              <p className="font-display font-[900] text-[15px] tracking-[0.4px] uppercase text-[#0F0F10] leading-none">
                {tier}
              </p>
              <p className="text-[11px] font-[600] text-[#6B7280] tracking-[-0.1px] mt-1">
                {tier === 'free' ? 'Upgrade' : 'Manage plan'}
              </p>
            </button>
          </div>
        </div>

        {/* ======================= 5.9 Inner Tabs ======================= */}
        <div className="flex -mx-4 px-4 mt-3 gap-6 border-b" style={{ borderColor: 'rgba(15,15,16,0.08)' }} data-testid="profile-v2-tabs">
          {[['plans', 'Plans'], ['safety', 'Safety']].map(([id, label]) => {
            const active = tab === id;
            return (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`profile-v2-tab-${id}`}
                className="flex-1 py-3 text-center relative"
                style={{
                  borderBottom: `2px solid ${active ? '#0F0F10' : 'transparent'}`,
                }}
              >
                <span
                  className="text-[16px] tracking-[-0.3px]"
                  style={{
                    fontWeight: active ? 900 : 700,
                    color: active ? '#0F0F10' : '#6B7280',
                  }}
                >
                  {label}
                </span>
              </button>
            );
          })}
        </div>

        {/* ======================= 5.10 Plans Tab ======================= */}
        {tab === 'plans' && (
          <div className="space-y-3" data-testid="profile-v2-plans-tab">
            {/* Score circles — 2x2 grid */}
            <div className="grid grid-cols-2 gap-3">
              <ScoreCircle
                testid="profile-v2-score-profile"
                score={profileScore?.score || 0}
                color="#B91C1C"
                kicker="Profile"
                subtitle={profileScore?.grade || 'Not scored'}
                onClick={() => navigate('/profile-score')}
              />
              <ScoreCircle
                testid="profile-v2-score-reputation"
                score={ghosting?.score || 100}
                color="#2563EB"
                kicker="Reputation"
                subtitle={ghosting?.label || 'New User'}
                onClick={() => navigate('/credibility')}
              />
              {v2Scores?.meet && (
                <ScoreCircle
                  testid="profile-v2-score-meet"
                  score={v2Scores.meet.score || 0}
                  color="#15803D"
                  kicker="Meet Score"
                  subtitle={v2Scores.meet.is_new ? 'Building history' : (v2Scores.meet.band || '—')}
                  onClick={() => navigate('/scoring-detail')}
                />
              )}
              {v2Scores?.responsiveness && (
                <ScoreCircle
                  testid="profile-v2-score-reliability"
                  score={v2Scores.responsiveness.score || 0}
                  color="#7C3AED"
                  kicker="Reply Reliability"
                  subtitle={v2Scores.responsiveness.is_new ? 'Building history' : (v2Scores.responsiveness.band || '—')}
                  onClick={() => navigate('/scoring-detail')}
                />
              )}
            </div>

            {/* Photos management grid — 6 slots */}
            <div className="bg-white rounded-2xl p-4 border border-[var(--hairline)]" data-testid="profile-v2-photos-grid">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-[700] tracking-[0.12em] uppercase text-[#6B7280]">Photos</p>
                <button onClick={() => navigate('/profile?edit=1')} className="text-[12px] font-[700] text-[#DC2626]">Manage</button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {Array.from({ length: 6 }).map((_, i) => {
                  const p = photos[i];
                  return (
                    <div
                      key={i}
                      className="aspect-square rounded-xl overflow-hidden bg-[#F4F4F5] flex items-center justify-center border border-[var(--hairline)]"
                      data-testid={`profile-v2-photo-slot-${i}`}
                    >
                      {p ? (
                        <img src={getPhotoUrl(p)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <button onClick={() => navigate('/profile?edit=1')} className="w-full h-full flex items-center justify-center text-[#6B7280]">
                          <Plus size={20} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Meeting Preferences card — BLUE #1E4FD8 */}
            <button
              onClick={() => navigate('/meeting-prefs')}
              data-testid="profile-v2-meeting-prefs-card"
              className="w-full flex items-center gap-3 bg-white border rounded-2xl p-4 hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition-shadow text-left"
              style={{ borderColor: 'rgba(30,79,216,0.25)' }}
            >
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(30,79,216,0.1)' }}>
                <Heart size={20} style={{ color: '#1E4FD8' }} fill="#1E4FD8"/>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-[900] text-[15px] tracking-[-0.2px]" style={{ color: '#1E4FD8' }}>Meeting Preferences</p>
                <p className="text-[12px] text-[#6B7280] mt-0.5">Set your intent, availability, and first-date style</p>
              </div>
              <ChevronRight size={18} style={{ color: '#1E4FD8' }}/>
            </button>

            {/* Premium promo carousel — FREE users only */}
            {isFree && (
              <div className="-mx-4" data-testid="profile-v2-promo-carousel">
                <div
                  className="flex overflow-x-auto snap-x snap-mandatory pb-1"
                  style={{ scrollbarWidth: 'none' }}
                  onScroll={(e) => {
                    const el = e.currentTarget;
                    const idx = Math.round(el.scrollLeft / el.clientWidth);
                    if (idx !== promoIdx) setPromoIdx(idx);
                  }}
                >
                  {/* Premium slide */}
                  <button
                    onClick={() => navigate('/subscription')}
                    className="min-w-full snap-center px-4"
                    data-testid="profile-v2-promo-premium"
                  >
                    <div className="rounded-2xl p-5 text-left"
                      style={{ background: 'linear-gradient(135deg, #1A1A1F, #0F0F14)' }}>
                      <p className="text-[10px] font-[800] tracking-[0.15em] text-[#FDE047] uppercase">Unlock More Matches</p>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <Star size={22} fill="#FDE047" className="text-[#FDE047]"/>
                        <p className="font-display text-[22px] font-[900] text-white leading-none tracking-tight">
                          Meet to Keep <span style={{ color: '#FDE047' }}>Premium</span>
                        </p>
                      </div>
                      <p className="text-[13px] text-white/80 mt-2">Boosts, filters, and see who likes you</p>
                    </div>
                  </button>
                  {/* VIP slide */}
                  <button
                    onClick={() => navigate('/subscription')}
                    className="min-w-full snap-center px-4"
                    data-testid="profile-v2-promo-vip"
                  >
                    <div className="rounded-2xl p-5 text-left"
                      style={{ background: 'linear-gradient(135deg, #5B21B6, #7C3AED)' }}>
                      <p className="text-[10px] font-[800] tracking-[0.15em] uppercase" style={{ color: '#E0E7FF' }}>Everything, Unlimited</p>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <Diamond size={22} className="text-[#E0E7FF]" fill="#E0E7FF"/>
                        <p className="font-display text-[22px] font-[900] text-white leading-none tracking-tight">
                          Meet to Keep <span style={{ color: '#E0E7FF' }}>VIP</span>
                        </p>
                      </div>
                      <p className="text-[13px] text-white/80 mt-2">Incognito, global search, unlimited everything</p>
                    </div>
                  </button>
                </div>
                {/* Dots */}
                <div className="flex items-center justify-center gap-1.5 mt-2">
                  {[0, 1].map((i) => (
                    <span
                      key={i}
                      className="transition-all rounded-full"
                      style={{
                        width: i === promoIdx ? 18 : 6,
                        height: 6,
                        background: i === promoIdx ? '#0F0F10' : '#D1D5DB',
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Feature comparison table */}
            <div className="bg-white rounded-2xl p-4 border border-[var(--hairline)]" data-testid="profile-v2-compare-table">
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-[var(--hairline)]">
                <p className="text-[12px] font-[700] tracking-[0.12em] uppercase text-[#6B7280]">Compare plans</p>
                <div className="flex gap-1">
                  {['FREE', 'PREMIUM', 'VIP'].map((t) => {
                    const isCur = tier.toUpperCase() === t;
                    return (
                      <span
                        key={t}
                        className="text-[11px] tracking-[0.05em] uppercase inline-block text-right"
                        style={{ width: 56, fontWeight: isCur ? 900 : 700, color: isCur ? '#0F0F10' : '#6B7280' }}
                      >
                        {t}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div>
                {COMPARE_ROWS.map((row, i) => {
                  const Icon = row.icon;
                  return (
                    <div
                      key={row.label}
                      className={`flex items-center gap-2.5 py-2.5 ${i < COMPARE_ROWS.length - 1 ? 'border-b border-[var(--hairline)]' : ''}`}
                    >
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: row.tint }}>
                        <Icon size={14} className="text-[#0F0F10]" />
                      </div>
                      <span className="flex-1 text-[13px] font-[600] text-[#0F0F10] tracking-[-0.2px] truncate">{row.label}</span>
                      {['free', 'premium', 'vip'].map((t) => {
                        const isCur = tier === t;
                        return (
                          <span
                            key={t}
                            className="text-[12px] text-right inline-block"
                            style={{ width: 56, fontWeight: isCur ? 900 : 600, color: isCur ? '#0F0F10' : '#6B7280' }}
                          >
                            {row[t]}
                          </span>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
              {tier !== 'vip' && (
                <button
                  onClick={() => navigate('/subscription')}
                  data-testid="profile-v2-compare-cta"
                  className="w-full mt-4 rounded-full text-white text-[15px] font-[900] tracking-[-0.2px] py-3.5 overflow-hidden"
                  style={{
                    background: tier === 'free'
                      ? 'linear-gradient(135deg, #FF5864, #B91C1C)'
                      : 'linear-gradient(135deg, #7C3AED, #5B21B6)',
                    boxShadow: tier === 'free' ? '0 6px 20px -4px rgba(185,28,28,0.45)' : '0 6px 20px -4px rgba(124,58,237,0.45)',
                  }}
                >
                  {tier === 'free' ? 'Upgrade to Premium' : 'Upgrade to VIP'}
                </button>
              )}
            </div>

            {/* Boosts & Super Likes shortcut card — gold */}
            <div
              className="rounded-2xl p-4 border"
              style={{ background: '#FEF3C7', borderColor: '#FDE68A' }}
              data-testid="profile-store-shortcut"
            >
              <p className="text-[10px] font-[700] tracking-[0.12em] uppercase" style={{ color: '#B45309' }}>Power-Ups</p>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <button
                  onClick={() => navigate('/store?highlight=boost')}
                  data-testid="profile-v2-shortcut-boost"
                  className="flex items-center gap-2 py-2.5 px-3 rounded-full bg-white text-[13px] font-[700] text-[#0F0F10] hover:shadow-sm transition-shadow"
                >
                  <Zap size={14} className="text-[#D97706]"/>
                  <span className="truncate">Meet Priority · boost</span>
                </button>
                <button
                  onClick={() => navigate('/store?highlight=super_like')}
                  data-testid="profile-v2-shortcut-super"
                  className="flex items-center gap-2 py-2.5 px-3 rounded-full bg-white text-[13px] font-[700] text-[#0F0F10] hover:shadow-sm transition-shadow"
                >
                  <Star size={14} className="text-[#D97706]"/>
                  <span className="truncate">+1 Super Like</span>
                </button>
              </div>
            </div>

            {/* Edit Profile pill */}
            <button
              onClick={() => navigate('/profile?edit=1')}
              data-testid="profile-v2-edit-cta-bottom"
              className="w-full rounded-full bg-[#0F0F10] text-white text-[14px] font-[700] py-3 mt-1"
            >
              Edit Profile
            </button>
          </div>
        )}

        {/* ======================= 5.11 Safety Tab ====================== */}
        {tab === 'safety' && (
          <div className="space-y-2" data-testid="profile-v2-safety-tab">
            {[
              { icon: ShieldCheck, label: 'Safety Center',        onClick: () => navigate('/safety'),      testid: 'safety-center' },
              { icon: Heart,       label: 'Safe Meet Check-In',   onClick: () => navigate('/safe-meet'),   testid: 'safe-meet' },
              { icon: CheckSquare, label: 'Verify Profile',       onClick: () => navigate('/verify'),      testid: 'verify' },
              { icon: ShieldBan,   label: 'Blocked Users',        onClick: () => navigate('/settings#blocked'), testid: 'blocked' },
              { icon: Eye,         label: 'Incognito mode',       onClick: () => navigate('/settings'),    testid: 'incognito', locked: tier !== 'vip' },
            ].map((row) => (
              <button
                key={row.label}
                onClick={row.onClick}
                data-testid={`profile-v2-safety-${row.testid}`}
                className="w-full flex items-center gap-3 bg-white border border-[var(--hairline)] rounded-2xl p-4 hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition-shadow"
              >
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#F4F4F5' }}>
                  <row.icon size={17} className="text-[#0F0F10]" strokeWidth={2}/>
                </div>
                <span className="flex-1 text-left text-[14px] font-[700] text-[#0F0F10]">{row.label}</span>
                {row.locked && <Lock size={14} className="text-[#6B7280]"/>}
                <ChevronRight size={16} className="text-[#6B7280]"/>
              </button>
            ))}

            {/* Legal & Support footer — Sprint D. Small text links, less prominent than the safety rows above. */}
            <div className="pt-6 mt-2" data-testid="profile-v2-legal-footer">
              <p className="text-[10px] font-[700] tracking-[0.12em] uppercase text-[#8A8A93] mb-2 px-1">
                Legal &amp; Help
              </p>
              <div className="bg-white rounded-2xl border border-[var(--hairline)] overflow-hidden">
                {[
                  { label: 'Contact Support', to: '/support',    testid: 'support' },
                  { label: 'Community Guidelines', to: '/guidelines', testid: 'guidelines' },
                  { label: 'Terms of Service', to: '/terms',     testid: 'terms' },
                  { label: 'Privacy Policy',   to: '/privacy',   testid: 'privacy' },
                  { label: 'Cookie Policy',    to: '/cookies',   testid: 'cookies' },
                ].map((r, i, arr) => (
                  <button
                    key={r.to}
                    onClick={() => navigate(r.to)}
                    data-testid={`profile-v2-legal-${r.testid}`}
                    className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#FAFAFA] transition-colors ${i < arr.length - 1 ? 'border-b border-[var(--hairline)]' : ''}`}
                  >
                    <span className="text-[13.5px] font-[600] text-[#0F0F10]">{r.label}</span>
                    <ChevronRight size={14} className="text-[#9CA3AF]"/>
                  </button>
                ))}
              </div>
              <p className="text-[11px] font-[600] text-[#9CA3AF] text-center mt-4">
                © {new Date().getFullYear()} Meet to Keep LLC
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
